import { getShipDefinition, SEQ_CHUNK_ANCHOR_POP, SEQ_CHUNK_BASE, SEQ_CHUNK_MAX, } from "@kaeliux/shared";
import { applyDamage, beginRound, buildStartOfRoundEvents, buildUnitGroups, calculateBattlePoints, calculateBattleScrap, calculateDemolitionChance, cloneGroups, computeHitDamage, computeLosses, critChanceForShip, criticalChanceFor, groupsToPlanetShips, initLifePool, initPassiveLayers, isGroupKind, megaPowerBonusFor, removeBonusLifeLayers, resolveFreezing, resolveRevival, resolveStartOfRoundSkills, rollHit, RollCursor, roundLossCounts, selectTarget, snapshotCounts, totalShipCount, } from "./battle.js";
/**
 * Sequential ("back-and-forth") battle resolution — the shipped main battle
 * system (`fleetResolution.ts` uses this model to resolve attack/pirate
 * combat, both in the mock path and via `apps/functions`). `battle.ts`'s
 * simultaneous `resolveBattle` is kept as a dev-only side-by-side comparison
 * on the Battle Simulator page and in `battleComparison.test.ts`.
 *
 * The naive sequential model (attacker fires its whole force first, removing
 * defenders, then the defender retaliates with only the survivors) hands the
 * attacker a first-strike edge that COMPOUNDS into a guaranteed sweep. This
 * version breaks that advantage with three levers, while keeping the
 * back-and-forth character:
 *
 *  1. One coin-toss initiative, then strict alternation — a single roll at
 *     the start of the battle decides who fires first in round 1; every round
 *     after that simply swaps who goes first. Over any multi-round battle
 *     each side gets an equal number of first-fire rounds (±1), so neither
 *     side is systematically favored, without needing a fresh roll every
 *     round.
 *  2. Population-scaled sub-chunking + fine interleaving — each side's force is
 *     split into `chunkCountForPopulation` sub-groups per ship type, and the
 *     two sides alternate one single-volley sub-chunk at a time. Only one
 *     sub-chunk's kills are missing before its counterpart retaliates, so the
 *     first-strike seed (and thus the snowball) is tiny.
 *  3. Single fire per sub-chunk — a sub-chunk fires exactly one volley at one
 *     target at the 100% hit-step; unlike the simultaneous model it does NOT
 *     take follow-up (66%/33%) hits or spill onto a second target.
 *
 * Everything else — targeting, per-hit damage, active skills, freezing,
 * revival, the life pool, and all post-battle math — reuses `battle.ts`'s
 * primitives, and the `BattleInput`/`BattleResult` shapes are identical, so
 * this stays a drop-in alternative.
 *
 * Passive bonuses (Bonus Life, Armor Boost) and the Devastate chance are
 * recalculated at the start of every round from the providers still alive, and
 * the Bonus Life layer is removed when the battle ends (see `beginRound`,
 * `removeBonusLifeLayers`).
 *
 * Roll order (documented so tests can be deterministic): one initiative roll
 * before round 1 (never re-rolled — later rounds alternate off it), then per
 * round: Mega Power activation (attacker, defender; only with a live provider)
 * -> freezing (attacker->def, def->att) -> per firing sub-chunk, in firing
 * order: one crit roll, then one Ignore Armor roll (only when the round's
 * Ignore Armor chance is above 0, the firing ship id is not an Ignore Armor
 * provider, and the sub-chunk is not frozen) -> revival (attacker, defender).
 */
/** Number of alternating sub-groups a ship-type group of `count` ships is split into. */
export function chunkCountForPopulation(count) {
    if (count <= 1)
        return Math.max(0, count);
    const raw = SEQ_CHUNK_BASE * 2 ** Math.log10(count / SEQ_CHUNK_ANCHOR_POP);
    const clamped = Math.min(SEQ_CHUNK_MAX, Math.max(SEQ_CHUNK_BASE, Math.round(raw)));
    return Math.min(clamped, count); // never more chunks than ships
}
/** Split `total` into `parts` as-equal-as-possible whole sizes (`10,4 -> [3,3,2,2]`). */
function splitEqually(total, parts) {
    const base = Math.floor(total / parts);
    const remainder = total % parts;
    return Array.from({ length: parts }, (_, i) => base + (i < remainder ? 1 : 0));
}
/** Ordered sub-chunks for one side, biggest-population groups first (existing group order). */
function buildSubChunks(groups) {
    const chunks = [];
    for (const group of groups) {
        if (group.count <= 0)
            continue;
        const parts = chunkCountForPopulation(group.count);
        for (const size of splitEqually(group.count, parts)) {
            if (size > 0)
                chunks.push({ group, plannedSize: size });
        }
    }
    return chunks;
}
/**
 * Fire a single sub-chunk once (100% hit-step, one target, no follow-up).
 * `firedThisRound` tracks how many of each group's ships have already fired this
 * round; casualties (the live `group.count` dropping from enemy volleys) shrink
 * what later sub-chunks of that group can still fire — the sequential
 * casualties-reduce-the-unfired-pool rule. Returns the log entry, or undefined
 * when the sub-chunk has no live unfired ships or the enemy is already wiped.
 */
function fireSubChunk(chunk, firingSideLabel, enemyGroups, round, skills, frozen, critChance, firedThisRound, rolls, lifePool) {
    const { group } = chunk;
    const alreadyFired = firedThisRound.get(group) ?? 0;
    const unfiredAlive = Math.max(0, group.count - alreadyFired);
    const firingCount = Math.min(chunk.plannedSize, unfiredAlive);
    if (firingCount <= 0)
        return undefined;
    const firingDef = getShipDefinition(group.race, group.shipId);
    const target = selectTarget(firingDef, enemyGroups);
    if (!target)
        return undefined; // enemy wiped — nothing to fire at
    firedThisRound.set(group, alreadyFired + firingCount);
    const isFrozen = isGroupKind(group, frozen);
    const megaPowerBonus = megaPowerBonusFor(skills, group.shipId);
    const groupCritChance = critChanceForShip(critChance, group.shipId);
    const targetLifeBefore = lifePool.get(target) ?? target.perUnit.life * target.count;
    const { criticalRoll, ignoreArmorActive } = rollHit(skills, group.shipId, isFrozen, rolls);
    const hit = computeHitDamage({ ...group, count: firingCount }, firingDef, target, 0, {
        megaPowerBonus,
        isFrozen,
        ignoreArmorActive,
        criticalChance: groupCritChance,
        criticalRoll,
    });
    const { destroyed } = applyDamage(target, hit.finalDamage, lifePool);
    return {
        round,
        firingSide: firingSideLabel,
        firingShipId: group.shipId,
        firingRace: group.race,
        firingCount,
        targetShipId: target.shipId,
        targetRace: target.race,
        hitStep: 0,
        rawDamage: hit.rawDamage,
        finalDamage: hit.finalDamage,
        unitsDestroyed: destroyed,
        wasCritical: hit.wasCritical,
        wasArmorIgnored: hit.wasArmorIgnored,
        wasFrozen: isFrozen,
        targetLifeBefore,
        targetArmorPercent: hit.wasArmorIgnored ? 0 : target.perUnit.armor,
    };
}
/**
 * Exported for `sunBattle.ts`'s pooled Sun Attack battle, which needs this
 * exact per-round resolution logic applied to a MERGED multi-fleet
 * defender-group array — it doesn't care whether `defenderGroups` came from
 * one `BattleSideInput` or several concatenated ones, so it is reusable
 * unmodified. Every other caller keeps going through
 * {@link resolveBattleSequential}. Callers build `layers` with
 * `initPassiveLayers` on the live groups and call `removeBonusLifeLayers` once
 * the last round is done, before reading survivors and losses.
 */
export function resolveRoundSequential(round, attackerFirst, attackerGroups, defenderGroups, attackerBaseCritChance, defenderBaseCritChance, rolls, lifePool, layers) {
    const attackerCritChance = beginRound(attackerGroups, layers, lifePool, attackerBaseCritChance);
    const defenderCritChance = beginRound(defenderGroups, layers, lifePool, defenderBaseCritChance);
    const startAttacker = snapshotCounts(attackerGroups);
    const startDefender = snapshotCounts(defenderGroups);
    // Snapshot forces AS THEY WERE at the start of this round for the report.
    const attackerGroupsAtStart = cloneGroups(attackerGroups);
    const defenderGroupsAtStart = cloneGroups(defenderGroups);
    const attackerSkills = resolveStartOfRoundSkills(attackerGroups, rolls);
    const defenderSkills = resolveStartOfRoundSkills(defenderGroups, rolls);
    const frozenDefender = resolveFreezing(attackerGroups, defenderGroups, rolls);
    const frozenAttacker = resolveFreezing(defenderGroups, attackerGroups, rolls);
    const events = buildStartOfRoundEvents(attackerSkills, defenderSkills, frozenDefender, frozenAttacker);
    // Break each side into sub-chunks and alternate single volleys, starting with
    // this round's first-mover (`attackerFirst`, set by the caller). Entries are
    // pushed in true firing order so the interleave is visible in the log. Damage
    // applies immediately, so a sub-chunk killed before its turn (its group
    // whittled down) fires fewer/no ships — this is what neutralizes the
    // first-mover's advantage.
    const attackerChunks = buildSubChunks(attackerGroups);
    const defenderChunks = buildSubChunks(defenderGroups);
    const firedThisRound = new Map();
    const entries = [];
    let ai = 0;
    let di = 0;
    let turnIsAttacker = attackerFirst;
    while (ai < attackerChunks.length || di < defenderChunks.length) {
        const attackerHasMore = ai < attackerChunks.length;
        const defenderHasMore = di < defenderChunks.length;
        // Fire the side whose turn it is; if that side is exhausted, the other drains.
        const fireAttacker = attackerHasMore && (turnIsAttacker || !defenderHasMore);
        if (fireAttacker) {
            const entry = fireSubChunk(attackerChunks[ai], "attacker", defenderGroups, round, attackerSkills, frozenAttacker?.frozen, attackerCritChance, firedThisRound, rolls, lifePool);
            if (entry)
                entries.push(entry);
            ai += 1;
        }
        else {
            const entry = fireSubChunk(defenderChunks[di], "defender", attackerGroups, round, defenderSkills, frozenDefender?.frozen, defenderCritChance, firedThisRound, rolls, lifePool);
            if (entry)
                entries.push(entry);
            di += 1;
        }
        turnIsAttacker = !turnIsAttacker;
    }
    const attackerLossesThisRound = roundLossCounts(attackerGroups, startAttacker);
    const defenderLossesThisRound = roundLossCounts(defenderGroups, startDefender);
    const revivalEvents = [
        ...resolveRevival(attackerGroups, "attacker", attackerLossesThisRound, rolls, lifePool),
        ...resolveRevival(defenderGroups, "defender", defenderLossesThisRound, rolls, lifePool),
    ];
    return {
        round,
        attackerGroups: attackerGroupsAtStart,
        defenderGroups: defenderGroupsAtStart,
        entries,
        events: [...events, ...revivalEvents],
    };
}
/**
 * Resolve a full battle with the rebalanced sequential ("back-and-forth") model.
 * Identical top-level flow to `resolveBattle` — only the per-round resolution
 * differs (see `resolveRoundSequential`). Loops rounds until `input.rounds` is
 * reached or one side has zero total ships.
 */
export function resolveBattleSequential(input, rolls) {
    const rollCursor = new RollCursor(rolls);
    const attackerStart = buildUnitGroups(input.attacker);
    const defenderStart = buildUnitGroups(input.defender);
    const attackerGroups = cloneGroups(attackerStart);
    const defenderGroups = cloneGroups(defenderStart);
    const attackerCritChance = criticalChanceFor(input.attacker, attackerGroups);
    const defenderCritChance = criticalChanceFor(input.defender, defenderGroups);
    const lifePool = initLifePool([...attackerGroups, ...defenderGroups]);
    const layers = initPassiveLayers([...attackerGroups, ...defenderGroups]);
    // One coin toss for the whole battle: who fires first in round 1. Every
    // later round simply swaps who goes first — no re-roll — so over any
    // multi-round battle each side gets an equal (±1) number of first-fire
    // rounds without needing fresh randomness every round.
    const round1AttackerFirst = rollCursor.next() < 0.5;
    const rounds = [];
    for (let round = 1; round <= input.rounds; round++) {
        if (totalShipCount(attackerGroups) === 0 || totalShipCount(defenderGroups) === 0)
            break;
        const attackerFirst = round % 2 === 1 ? round1AttackerFirst : !round1AttackerFirst;
        rounds.push(resolveRoundSequential(round, attackerFirst, attackerGroups, defenderGroups, attackerCritChance, defenderCritChance, rollCursor, lifePool, layers));
    }
    removeBonusLifeLayers([...attackerGroups, ...defenderGroups], layers, lifePool);
    const attackerSurvivors = groupsToPlanetShips(attackerGroups);
    const defenderSurvivors = groupsToPlanetShips(defenderGroups);
    const attackerLosses = computeLosses(attackerStart, attackerGroups);
    const defenderLosses = computeLosses(defenderStart, defenderGroups);
    const points = calculateBattlePoints(input.attacker.race, attackerLosses, input.defender.race, defenderLosses);
    const scrap = calculateBattleScrap(attackerLosses, input.attacker.race, defenderLosses, input.defender.race, rollCursor.next());
    const attackerTotal = totalShipCount(attackerGroups);
    const defenderTotal = totalShipCount(defenderGroups);
    const outcome = attackerTotal > 0 && defenderTotal === 0 ? "attackerWins" :
        defenderTotal > 0 && attackerTotal === 0 ? "defenderWins" :
            "draw";
    return {
        rounds,
        attackerSurvivors,
        defenderSurvivors,
        attackerLosses,
        defenderLosses,
        points,
        scrap,
        demolitionPoints: {
            attackerChance: calculateDemolitionChance(input.attacker.race, attackerSurvivors),
            defenderChance: calculateDemolitionChance(input.defender.race, defenderSurvivors),
        },
        outcome,
        passivesPerRound: true,
    };
}
//# sourceMappingURL=battleSequential.js.map